<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <div v-if="loading" class="h-full flex items-center justify-center text-xs space-x-2">
                <Loader class="w-4 h-4 animate-spin" />
                <span>Loading...</span>
            </div>
            <template v-else>
                <DialogHeader>
                    <DialogTitle>Edit Cloudflare Server</DialogTitle>
                </DialogHeader>
                <div class="space-y-3">
                    <FormField name="title" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>Title (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="Title" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_token" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API Token (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API Token" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField name="api_url" v-slot="{ componentField }">
                        <FormItem>
                            <FormLabel>API URL (Required)</FormLabel>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="API URL" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        :disabled="isCreatePending"
                        @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="button"
                        :loading="isCreatePending"
                        :disabled="!meta.valid || !meta.dirty"
                        @click="submit"
                        >Save</Button
                    >
                </DialogFooter>
            </template>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { setFieldsError } from '@/lib/utils'
import { useUpdateCloudflareServerMutation } from '@/queries/cloudflare'
import httpClient from '@/utils/httpClient'
import { toTypedSchema } from '@vee-validate/zod'
import { Loader } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { z } from 'zod'

const props = defineProps<{
    server: {
        id: string
    }
}>()

const route = useRoute()

const open = defineModel()

const { mutate: updateServer, isPending: isCreatePending } = useUpdateCloudflareServerMutation()

const loading = ref(false)

const schema = z.object({
    title: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    api_url: z
        .string()
        .min(1, {
            message: "You can't leave this empty.",
        })
        .url({
            message: 'Url must be a valid URL.',
        }),
    api_token: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const meta = computed(() => form.meta.value)

const submit = form.handleSubmit(async (values) => {
    if (!meta.value.dirty || !meta.value.valid) return

    const payload = {
        ...values,
        id: props.server.id,
    }

    updateServer(payload, {
        onSuccess: () => {
            close()
        },
        onError: (error) => {
            console.log(error)
            setFieldsError(error, form.setFieldError)
        },
    })
})

const init = async () => {
    if (!open.value) return

    try {
        loading.value = true

        const { data } = await httpClient.get(`cloudflare/servers/${route.params.serverId}/show`)

        if (data) {
            form.resetForm({
                values: {
                    id: props.server.id,
                    title: data.title,
                    api_token: data.api_token,
                    api_url: data.api_url,
                },
            })
        }
    } catch (e) {
        console.log(e)
    } finally {
        loading.value = false
    }
}

const close = () => {
    open.value = false
    form.resetForm()
}

watch(open, (val) => {
    if (val) init()
})

onMounted(() => {
    if (open.value) {
        init()
    }
})
</script>
